
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Community() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formData = new FormData(formElement);

      const urlEncodedData = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d60b270iku00ff9vl23g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <h2
            className="text-5xl sm:text-6xl lg:text-7xl font-normal text-gray-900 mb-6 uppercase tracking-tight"
            style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
          >
            {t('community.title')}
          </h2>
          <div className="w-24 h-1 bg-amber-700 mx-auto mb-8"></div>
          <p
            className="text-xl sm:text-2xl font-bold text-amber-700 mb-4"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {t('community.subtitle')}
          </p>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            {t('community.description')}
          </p>
        </div>

        {/* Newsletter Signup Card */}
        <div className="bg-white rounded-xl shadow-xl border-2 border-stone-200 overflow-hidden max-w-xl mx-auto">
          <div className="bg-gradient-to-br from-amber-700 to-orange-700 p-6 sm:p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-white/20 rounded-full">
              <i className="ri-mail-send-line text-4xl text-white"></i>
            </div>
            <h3
              className="text-2xl sm:text-3xl font-normal text-white uppercase mb-2"
              style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}
            >
              {t('community.joinTitle')}
            </h3>
            <p className="text-white/95">{t('community.deliveredBiweekly')}</p>
          </div>

          <div className="p-6 sm:p-8">
            <ul className="space-y-3 mb-6 text-sm">
              <li className="flex items-start gap-2 text-stone-700">
                <i className="ri-check-line text-amber-700 text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>{t('community.feature.liveEvents')}</strong> {t('community.feature.liveEventsDesc')}
                </span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <i className="ri-check-line text-amber-700 text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>{t('community.feature.festivals')}</strong> {t('community.feature.festivalsDesc')}
                </span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <i className="ri-check-line text-amber-700 text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>{t('community.feature.musicScenes')}</strong> {t('community.feature.musicScenesDesc')}
                </span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <i className="ri-check-line text-amber-700 text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>{t('community.feature.lifestyleBrands')}</strong> {t('community.feature.lifestyleBrandsDesc')}
                </span>
              </li>
              <li className="flex items-start gap-2 text-stone-700">
                <i className="ri-check-line text-amber-700 text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>{t('community.feature.culturalMomentum')}</strong> {t('community.feature.culturalMomentumDesc')}
                </span>
              </li>
            </ul>

            {status === 'success' && (
              <div className="mb-4 p-3 bg-green-50 border border-green-400 text-green-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span className="font-bold text-sm">
                    {t('community.successMessage')}
                  </span>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-4 p-3 bg-red-50 border border-red-400 text-red-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-fill text-lg"></i>
                  <span className="font-bold text-sm">
                    {t('community.errorMessage')}
                  </span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="ranchfire-live-home-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('community.emailPlaceholder')}
                  required
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-stone-300 focus:border-amber-700 focus:outline-none text-stone-900 text-base"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-amber-700 hover:bg-amber-800 text-white px-6 py-3 rounded-lg font-bold text-sm uppercase tracking-wide transition-all duration-300 hover:shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? t('community.subscribing') : t('community.subscribe')}
                </button>
              </div>
            </form>

            <p className="text-center text-stone-500 text-xs mt-4">
              {t('community.unsubscribeNote')}
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/events"
            onClick={() => window.scrollTo(0, 0)}
            className="text-amber-700 hover:text-amber-800 font-medium text-sm inline-flex items-center gap-1 cursor-pointer"
          >
            {t('community.learnMore')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
